/**
 * Cursor Sparkle Easter Egg
 *
 * Creates a magical sparkle trail that follows the cursor
 * when viewing a "first note" after the Genesis Event.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { GENESIS_COLORS } from '../types';

interface Sparkle {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  createdAt: number;
}

interface CursorSparkleProps {
  /** Whether sparkles should be active */
  active: boolean;
  /** Duration in ms before auto-disabling (0 = infinite) */
  duration?: number;
}

const SPARKLE_COLORS = [
  GENESIS_COLORS.primary,
  GENESIS_COLORS.secondary,
  GENESIS_COLORS.accent,
  '#FFD700',
  '#ec4899',
  '#06b6d4',
];

const CursorSparkle = ({ active, duration = 5000 }: CursorSparkleProps) => {
  const [sparkles, setSparkles] = useState<Sparkle[]>([]);
  const [isActive, setIsActive] = useState(active);
  const sparkleIdRef = useRef(0);
  const lastSparkleRef = useRef(0);
  const startTimeRef = useRef(0);
  const cleanupIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Deterministic interval cleanup on unmount — prevents portal removeChild race
  useEffect(() => {
    return () => {
      if (cleanupIntervalRef.current) clearInterval(cleanupIntervalRef.current);
    };
  }, []);

  // Create sparkle at position
  const createSparkle = useCallback(
    (x: number, y: number) => {
      const now = Date.now();

      // Throttle sparkle creation (one every 30ms)
      if (now - lastSparkleRef.current < 30) return;
      lastSparkleRef.current = now;

      // Check duration
      if (duration > 0 && now - startTimeRef.current > duration) {
        setIsActive(false);
        return;
      }

      const sparkle: Sparkle = {
        id: sparkleIdRef.current++,
        x: x + (Math.random() - 0.5) * 20,
        y: y + (Math.random() - 0.5) * 20,
        color: SPARKLE_COLORS[Math.floor(Math.random() * SPARKLE_COLORS.length)],
        size: 6 + Math.random() * 8,
        createdAt: now,
      };

      setSparkles((prev) => [...prev.slice(-30), sparkle]); // Keep max 30 sparkles
    },
    [duration],
  );

  // Mouse move handler
  useEffect(() => {
    if (!isActive) return;

    startTimeRef.current = Date.now();

    const handleMouseMove = (e: MouseEvent) => {
      createSparkle(e.clientX, e.clientY);
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isActive, createSparkle]);

  // Cleanup old sparkles
  useEffect(() => {
    if (sparkles.length === 0) return;

    const interval = setInterval(() => {
      const now = Date.now();
      setSparkles((prev) => prev.filter((s) => now - s.createdAt < 600));
    }, 100);
    cleanupIntervalRef.current = interval;

    return () => {
      clearInterval(interval);
      cleanupIntervalRef.current = null;
    };
  }, [sparkles.length]);

  // Sync with active prop
  useEffect(() => {
    setIsActive(active);
    if (active) {
      startTimeRef.current = Date.now();
    }
  }, [active]);

  if (!isActive && sparkles.length === 0) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 99999,
        overflow: 'hidden',
      }}
    >
      {sparkles.map((sparkle) => {
        const age = Date.now() - sparkle.createdAt;
        const progress = age / 600;
        const opacity = 1 - progress;
        const scale = 1 - progress * 0.5;

        return (
          <div
            key={sparkle.id}
            style={{
              position: 'absolute',
              left: sparkle.x,
              top: sparkle.y,
              width: sparkle.size,
              height: sparkle.size,
              borderRadius: '50%',
              backgroundColor: sparkle.color,
              boxShadow: `0 0 ${sparkle.size}px ${sparkle.color}, 0 0 ${sparkle.size * 2}px ${sparkle.color}`,
              transform: `translate(-50%, -50%) scale(${scale}) rotate(${progress * 180}deg)`,
              opacity,
              pointerEvents: 'none',
            }}
          />
        );
      })}
    </div>,
    document.body,
  );
};

export default CursorSparkle;
